import React, { useState, useEffect, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  Box, Typography, Button, IconButton, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, Tooltip, CircularProgress,
  Snackbar, Checkbox, ListItemText,
  Accordion, AccordionSummary, AccordionDetails
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import VisibilityIcon from "@material-ui/icons/Visibility";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RepeatIcon from "@material-ui/icons/Repeat";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import ImageIcon from "@material-ui/icons/Image";
import VideocamIcon from "@material-ui/icons/Videocam";
import { AuthContext } from "../../context/Auth/AuthContext";
import AiVideoModal from "../AiVideo/AiVideoModal";
import GroupIcon from "@material-ui/icons/Group";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";
import SyncIcon from "@material-ui/icons/Sync";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import moment from "moment-timezone";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import Paper from "@material-ui/core/Paper";
import FileManagerSelectorModal from "../../components/FileManagerSelectorModal";
import AiContentFactory from "../../components/AiContentFactory";
import AiPromptViewer from "../../components/AiPromptViewer";
import PageHelpGuide from "../../components/PageHelpGuide";
import { GROUP_HELP_DATA } from "../../constants/pageHelp/groupHelp";
import StandardTabs from "../../components/StandardTabs";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  cyanButton: {
    ...getStandardButtonStyles(theme).applyButton,
  },
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 20px ${theme.palette.primary.main}80 !important` 
      : `0 0 16px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#FFFFFF !important",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden !important",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100%",
    borderRadius: 16,
    backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingTop: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  root: {
    padding: theme.spacing(3),
    maxWidth: 1400,
    margin: "0 auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    fontSize: "1.4rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  exclusiveBadge: {
    backgroundColor: `${theme.palette.primary.main}26`,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}80`,
    borderRadius: 20,
    padding: "2px 12px",
    fontSize: "0.72rem",
    fontWeight: 700,
    marginLeft: theme.spacing(1),
  },
  dayColumn: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    minHeight: 200,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  dayHeader: {
    fontWeight: 700,
    textAlign: "center",
    padding: theme.spacing(1),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  scheduleCard: {
    border: "1px solid",
    borderColor: "rgba(255, 255, 255, 0.8)",
    boxShadow: "0 0 8px rgba(255,255,255,0.4)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    transition: "box-shadow 0.2s, border-color 0.2s",
    "&:hover": { 
      boxShadow: `0 0 15px ${theme.palette.primary.main}99`,
      borderColor: theme.palette.primary.main,
    },
  },
  inactiveCard: {
    opacity: 0.9,
    borderStyle: "solid",
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  repeatBadge: {
    backgroundColor: "#1976d2",
    color: "#fff",
    borderRadius: 20,
    padding: "1px 8px",
    fontSize: "0.65rem",
    marginLeft: 4,
  },
  colorSwatch: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    display: "inline-block",
    border: "1px solid #ccc",
    verticalAlign: "middle",
    marginRight: 4,
  },
  fileInputRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  sentBadge: {
    backgroundColor: "#00BFA5",
    color: "#fff",
    borderRadius: 4,
    padding: "2px 6px",
    fontSize: "0.75rem",
    fontWeight: 700,
    display: "inline-block",
  },
  infoBox: {
    backgroundColor: "#e3f2fd",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    fontSize: "0.82rem",
    color: "#1565c0",
  },
  weekHeader: {
    backgroundColor: "rgba(0, 191, 165, 0.1)",
    borderBottom: "1px solid rgba(0, 191, 165, 0.3)",
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: "transparent",
  }
}));

export default function GroupScheduler() {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { get: getCompanySetting } = useCompanySettings();
  const [companyTimezone, setCompanyTimezone] = useState("America/Lima");

  const [connections, setConnections] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("all");
  const [previewMedia, setPreviewMedia] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [restrictedGroups, setRestrictedGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [aiVideoOpen, setAiVideoOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectorModalOpen, setSelectorModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [syncingGroups, setSyncingGroups] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [aiFactoryOpen, setAiFactoryOpen] = useState(false);
  const [aiEditSchedule, setAiEditSchedule] = useState(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.type === "dark";
  const isAdmin = Boolean(user?.super || (user?.profile === "admin" && user?.companyId === 1));

  const SCHEDULER_TAB_DEFINITIONS = [
    {
      key: "manual",
      labelKey: "statusScheduler.tabs.manual",
      defaultLabel: "Publicaciones Manuales",
      color: "#00E676",
      Icon: EditIcon,
    },
    {
      key: "aiImage",
      labelKey: "statusScheduler.tabs.aiImage",
      defaultLabel: "Publicaciones con IA (Imagen)",
      color: "#00E5FF",
      Icon: ImageIcon,
    },
    {
      key: "aiVideo",
      labelKey: "statusScheduler.tabs.aiVideo",
      defaultLabel: "Publicaciones con IA (Video)",
      color: "#E040FB",
      Icon: VideocamIcon,
      isBeta: true,
    },
  ];

  const handleTabChange = (e, newValue) => {
    if (newValue === 2 && !isAdmin) {
      toastError(
        i18n.t("statusScheduler.tabs.aiVideoBetaRestricted") ||
        "Esta funcionalidad con IA de Video se encuentra en fase Beta exclusiva para el administrador."
      );
      return;
    }
    setTabValue(newValue);
  };

  const renderSchedulerTabLabel = (index) => {
    const tabDef = SCHEDULER_TAB_DEFINITIONS[index];
    const isActive = tabValue === index;
    const isVideoRestricted = tabDef.isBeta && !isAdmin;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          color: isActive
            ? (isDarkMode ? "#ffffff" : "#111827")
            : (isDarkMode ? "rgba(255, 255, 255, 0.65)" : "rgba(0, 0, 0, 0.6)"),
          transition: "all 0.25s ease",
          userSelect: "none",
          opacity: isVideoRestricted ? 0.75 : 1,
        }}
      >
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 12px" }}>
          <tabDef.Icon
            style={{
              marginRight: 8,
              color: tabDef.color,
              filter: isActive ? `drop-shadow(0 0 3px ${tabDef.color})` : "none",
              opacity: isActive ? 1 : 0.65,
              fontSize: "1.35rem",
              transition: "all 0.25s ease",
            }}
          />
          <span style={{
            fontWeight: isActive ? 900 : 600,
            lineHeight: 1, // Previene expansión vertical
            textShadow: isActive ? (isDarkMode ? "0 0 3px rgba(255, 255, 255, 0.4)" : "none") : "none",
            letterSpacing: "0.2px",
            whiteSpace: "nowrap",
          }}>
            {i18n.t(tabDef.labelKey) || tabDef.defaultLabel}
          </span>
          {tabDef.isBeta && (
            <span
              style={{
                marginLeft: 8,
                padding: "2px 7px",
                borderRadius: 10,
                fontSize: "0.65rem",
                fontWeight: 800,
                letterSpacing: "0.5px",
                background: "linear-gradient(135deg, #FF6D00 0%, #FF1744 100%)",
                color: "#ffffff",
                boxShadow: "0 0 5px rgba(255, 109, 0, 0.4)",
                textTransform: "uppercase",
                lineHeight: 1.2,
              }}
            >
              Beta
            </span>
          )}
        </div>
        <div style={{
          height: 3,
          minHeight: 3,
          flexShrink: 0,
          width: "100%",
          backgroundColor: isActive ? tabDef.color : "transparent",
          boxShadow: isActive ? `0 0 6px ${tabDef.color}` : "none",
          borderRadius: "3px 3px 0 0",
          transition: "all 0.25s ease",
          marginBottom: isActive ? "2px" : "0px", // Evita recorte en el desbordamiento
        }} />
      </div>
    );
  };
  const [viewSchedule, setViewSchedule] = useState(null);
  const [form, setForm] = useState({
    whatsappId: "",
    groupIds: [],
    scheduledAt: moment().tz("America/Lima").format("YYYY-MM-DDTHH:mm"),
    mediaType: "text",
    caption: "",
    mediaUrl: "",
    isActive: true,
    mentionAll: false,
    sendInterval: 15,
    // [ANTIGRAVITY] Integración IA
    aiGeneration: false,
    aiPrompt: ""
  });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [importBulkOpen, setImportBulkOpen] = useState(false);
  const [importWeekSchedules, setImportWeekSchedules] = useState([]);
  const [importBulkForm, setImportBulkForm] = useState({ whatsappId: "", groupIds: [] });
  const [importingBulk, setImportingBulk] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [importDestWeekStart, setImportDestWeekStart] = useState(null);
  const bulkFileInputRef = React.useRef(null);
  const nowTz = moment().tz(companyTimezone);
  const [selectedYear, setSelectedYear] = useState(nowTz.year());
  const [selectedMonth, setSelectedMonth] = useState(nowTz.month());

  useEffect(() => {
    getCompanySetting({ column: "timezone" }).then(res => {
      if (res && res.timezone) {
        setCompanyTimezone(res.timezone);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/whatsapp?session=0").then(({ data }) => {
      const baileys = (data || []).filter(
        w => !w.channel || w.channel === "whatsapp"
      );
      setConnections(baileys);
      if (baileys.length > 0) setSelectedWhatsapp("all");
    });

    const fetchGroups = async () => {
      try {
        let allGroups = [];
        let pageNumber = 1;
        let hasMore = true;
        
        while (hasMore) {
          const { data } = await api.get("/contacts", {
            params: { isGroup: "true", pageNumber }
          });
          allGroups = [...allGroups, ...data.contacts];
          hasMore = data.hasMore;
          pageNumber++;
        }
        setAvailableGroups(allGroups.filter(g => g.number));
      } catch (err) {
        console.error("Error fetching groups", err);
      }
    };
    fetchGroups();
  }, []);

  const handleVideoGenerated = async (videoUrl, prompt) => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") return;
    try {
      const payload = {
        groupIds: [],
        scheduledAt: new Date().toISOString(),
        mediaType: "video",
        mediaUrl: videoUrl,
        caption: "",
        aiGeneration: true,
        aiPrompt: prompt || "Video Generado",
        mentionAll: false,
        sendInterval: 15,
        isActive: false, // Guardado como borrador
        isCommunity: false, // it's group, not community
        whatsappId: selectedWhatsapp
      };
      await api.post("/group-schedule", payload);
      loadSchedules();
    } catch (err) {
      console.error("Error creating video draft", err);
    }
  };

  const loadMetadata = useCallback(async () => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      setRestrictedGroups({});
      return;
    }
    try {
      const { data } = await api.get(`/group-schedule/metadata/${selectedWhatsapp}`);
      setRestrictedGroups(data);
    } catch (err) {
      console.error("Error loading group metadata", err);
    }
  }, [selectedWhatsapp]);

  const fetchGroups = async () => {
    try {
      let allGroups = [];
      let pageNumber = 1;
      let hasMore = true;
      
      while (hasMore) {
        const { data } = await api.get("/contacts", {
          params: { isGroup: "true", pageNumber }
        });
        allGroups = [...allGroups, ...data.contacts];
        hasMore = data.hasMore;
        pageNumber++;
      }
      setAvailableGroups(allGroups.filter(g => g.number && g.active !== false));
    } catch (err) {
      console.error("Error fetching groups", err);
    }
  };

  const handleSyncGroups = async () => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") return;
    setSyncingGroups(true);
    try {
      const { data } = await api.post(`/whatsapp/${selectedWhatsapp}/sync-groups`);
      const hiddenCount = data.deactivatedCount || 0;
      const successMsg = i18n.t("statusScheduler.syncCompleteHidden", { count: hiddenCount });
      showSnack(successMsg, "success");
      await fetchGroups(); // Recargar la lista de grupos tras sincronizar
      await loadMetadata(); // Recargar los metadatos de restricción
    } catch (err) {
      console.error("Error al sincronizar grupos", err);
      showSnack(i18n.t("statusScheduler.syncError"), "error");
    } finally {
      setSyncingGroups(false);
    }
  };

  const loadSchedules = useCallback(async () => {
    if (!selectedWhatsapp) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/group-schedule?whatsappId=${selectedWhatsapp}&isCommunity=false`);
      const sortedData = data.sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
      setSchedules(sortedData);
    } catch (err) {
      showSnack(i18n.t("statusScheduler.errorLoad") || "Error al cargar las programaciones", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedWhatsapp]);

  useEffect(() => {
    if (selectedWhatsapp) {
      loadSchedules();
      loadMetadata();
    }
  }, [selectedWhatsapp, loadSchedules, loadMetadata]);

  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const onCompanyGroupSchedule = (data) => {
      if (data.schedule && data.schedule.isCommunity) return; // Ignore community updates

      if (data.action === "update" || data.action === "create") {
        setSchedules((prev) => {
          const idx = prev.findIndex(s => s.id === data.schedule.id);
          let newSchedules = [...prev];
          if (idx !== -1) {
            newSchedules[idx] = data.schedule;
          } else {
            newSchedules.push(data.schedule);
          }
          return newSchedules.sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
        });
      }

      if (data.action === "delete") {
        setSchedules((prev) => prev.filter(s => s.id !== +data.scheduleId));
      }
      
      if (data.action === "clone") {
        loadSchedules();
      }
    };

    socket.on(`company-${user.companyId}-groupSchedule`, onCompanyGroupSchedule);

    return () => {
      socket.off(`company-${user.companyId}-groupSchedule`, onCompanyGroupSchedule);
    };
  }, [socket, user?.companyId, loadSchedules]);
  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  const getErrorMessage = (err, defaultMsgKey = "statusScheduler.errorSave") => {
    const errorMsg = err?.response?.data?.error;
    if (errorMsg && i18n.exists(`backendErrors.${errorMsg}`)) {
      return i18n.t(`backendErrors.${errorMsg}`);
    }
    return errorMsg || err?.message || i18n.t(defaultMsgKey) || "Error al guardar";
  };

  const handleExportWeek = (weekSchedules, weekName) => {
    if (!weekSchedules || weekSchedules.length === 0) return showSnack("No hay tarjetas para exportar.", "warning");
    const data = weekSchedules.map(s => {
      const copy = { ...s };
      delete copy.id; delete copy.createdAt; delete copy.updatedAt; delete copy.status;
      return copy;
    });
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `grupos_${weekName.replace(/[\s/]+/g, '_')}.json`;
    a.click();
  };

  const handleImportBulkFile = (e, destWeekStart = null) => {
    const file = e.target.files[0];
    const inputTarget = e.target;
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (!Array.isArray(imported)) throw new Error("El archivo no es una exportacion valida.");
        if (imported.length === 0) throw new Error("El archivo esta vacio.");

        const currentTz = moment().tz(companyTimezone);

        // Validar que el destino no sea pasado
        if (destWeekStart) {
          const weekEnd = destWeekStart.clone().endOf('isoWeek');
          if (weekEnd.isBefore(currentTz, 'day')) {
            showSnack("No puedes importar en una semana que ya finalizo.", "error");
            if (inputTarget) inputTarget.value = ""; return;
          }
        } else {
          const destMonthEnd = moment().tz(companyTimezone).year(selectedYear).month(selectedMonth).endOf('month');
          if (destMonthEnd.isBefore(currentTz, 'day')) {
            showSnack("No puedes importar en un mes que ya finalizo.", "error");
            if (inputTarget) inputTarget.value = ""; return;
          }
        }

        // Motor Delta Mapping
        const sorted = [...imported].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
        const baseOriginal = moment(sorted[0].scheduledAt).tz(companyTimezone);
        const baseDestino = destWeekStart
          ? (destWeekStart.isSame(currentTz, 'day') || destWeekStart.isBefore(currentTz) ? currentTz.clone() : destWeekStart.clone().startOf('isoWeek'))
          : currentTz.clone();

        let clampedCount = 0;
        let lastValidDate = currentTz.clone().add(4, 'minutes');
        let currentPushOffset = 0;
        const baseOrigDay = baseOriginal.clone().startOf('day');

        const projected = sorted.map(s => {
          const origDate = moment(s.scheduledAt).tz(companyTimezone);
          const origDay = origDate.clone().startOf('day');
          const deltaDays = origDay.diff(baseOrigDay, 'days');
          const deltaMinutes = origDate.hours() * 60 + origDate.minutes();
          
          let mappedDate = baseDestino.clone().startOf('day').add(deltaDays, 'days').add(deltaMinutes, 'minutes');

          // Aplicamos el desfase actual a todas para no romper el espaciado interno del bloque
          mappedDate.add(currentPushOffset, 'milliseconds');

          if (mappedDate.isBefore(lastValidDate)) {
             clampedCount++;
             const extraPush = lastValidDate.diff(mappedDate);
             currentPushOffset += extraPush;
             mappedDate.add(extraPush, 'milliseconds');
          }

          lastValidDate = mappedDate.clone().add(1, 'minute');
          return { ...s, _projectedDate: mappedDate };
        });

        // Resumen por semana destino
        const byWeek = {};
        projected.forEach(s => {
          const weekLabel = `${i18n.t("statusScheduler.weekWord")} del ${s._projectedDate.clone().startOf('isoWeek').format('DD MMM')} al ${s._projectedDate.clone().endOf('isoWeek').format('DD MMM')}`;
          if (!byWeek[weekLabel]) byWeek[weekLabel] = 0;
          byWeek[weekLabel]++;
        });

        const isSameCompany = sorted.every(s => s.companyId === user?.companyId);
        const origFirstDate = moment(sorted[0].scheduledAt).tz(companyTimezone);
        const origLastDate = moment(sorted[sorted.length - 1].scheduledAt).tz(companyTimezone);
        const originLabel = `${origFirstDate.format('DD MMM YYYY')} - ${origLastDate.format('DD MMM YYYY')}`;
        const destLabel = destWeekStart
          ? `${i18n.t("statusScheduler.weekWord")} del ${destWeekStart.clone().startOf('isoWeek').format('DD MMM')} al ${destWeekStart.clone().endOf('isoWeek').format('DD MMM YYYY')}`
          : `${moment().month(selectedMonth).format('MMMM')} ${selectedYear}`;

        setImportSummary({ total: sorted.length, byWeek, destLabel, originLabel, clampedCount, projected, isSameCompany });
        setImportDestWeekStart(destWeekStart);
        setImportWeekSchedules(sorted);
        setImportBulkForm({ whatsappId: selectedWhatsapp === "all" ? "" : selectedWhatsapp, groupIds: [] });
        setImportBulkOpen(true);
      } catch (err) {
        showSnack("Error: " + err.message, "error");
      }
    };
    reader.readAsText(file);
    if (inputTarget) inputTarget.value = "";
  };

  const processBulkImport = async () => {
    if (!importBulkForm.whatsappId) return showSnack("Selecciona una conexion", "error");
    if (importBulkForm.groupIds.length === 0) return showSnack("Selecciona al menos un grupo", "error");
    if (!importSummary || !importSummary.projected) return showSnack("No hay datos para importar.", "error");

    setImportingBulk(true);
    let successCount = 0;
    for (let s of importSummary.projected) {
      try {
        const { _projectedDate, id, companyId, createdAt, updatedAt, ...rest } = s;
        const payload = {
          ...rest,
          whatsappId: importBulkForm.whatsappId,
          groupIds: importBulkForm.groupIds,
          mediaUrl: importSummary.isSameCompany ? (s.mediaUrl || "") : "",
          isActive: false,
          isCommunity: false,
          status: "PENDING",
          scheduledAt: _projectedDate.toISOString()
        };
        await api.post("/group-schedule", payload);
        successCount++;
      } catch (e) {
        console.error("Error importing schedule", e);
      }
    }
    setImportingBulk(false);
    setImportBulkOpen(false);
    setImportSummary(null);
    showSnack(`Se importaron ${successCount} de ${importSummary.projected.length} tarjetas exitosamente. Todas Inactivas.`);
    loadSchedules();
  };

  const openAdd = () => {
    setForm({
      whatsappId: selectedWhatsapp === "all" ? "" : selectedWhatsapp,
      groupIds: [],
      scheduledAt: moment().tz(companyTimezone).format("YYYY-MM-DDTHH:mm"),
      mediaType: "text",
      caption: "",
      mediaUrl: "",
      isActive: true,
      mentionAll: false,
      sendInterval: 15,
      aiGeneration: false,
      aiPrompt: ""
    });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      whatsappId: s.whatsappId,
      groupIds: s.groupIds || [],
      scheduledAt: moment(s.scheduledAt).tz(companyTimezone).format("YYYY-MM-DDTHH:mm"),
      mediaType: s.mediaType,
      caption: s.caption || "",
      mediaUrl: s.mediaUrl || "",
      isActive: s.isActive,
      mentionAll: s.mentionAll || false,
      sendInterval: s.sendInterval || 15,
      status: s.status
    });
    setModalOpen(true);
  };

  const openClone = (s) => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      showSnack("Selecciona una conexion de WhatsApp antes de clonar.", "warning");
      return;
    }
    setEditingId(null);
    if (s.aiGeneration) {
      setAiEditSchedule({ ...s, id: null });
      setAiFactoryOpen(true);
      return;
    }
    setForm({
      groupIds: s.groupIds || [],
      scheduledAt: moment().tz(companyTimezone).add(5, 'minutes').format("YYYY-MM-DDTHH:mm"), // default to +5 min
      mediaType: s.mediaType,
      caption: s.caption || "",
      mediaUrl: s.mediaUrl || "",
      isActive: false, // Siempre se clona como borrador inactivo
      mentionAll: s.mentionAll || false,
      sendInterval: s.sendInterval || 15,
      whatsappId: s.whatsappId || "",
      aiGeneration: s.aiGeneration || false,
      aiPrompt: s.aiPrompt || ""
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (form.groupIds.length === 0) throw new Error(i18n.t("statusScheduler.errorSelectGroup") || "Debe seleccionar al menos un grupo.");
      if (!form.scheduledAt) throw new Error(i18n.t("statusScheduler.errorSelectDate") || "Debe seleccionar fecha y hora.");
      
      // Bloquear cualquier fecha en el pasado Y también el minuto actual.
      // El usuario debe seleccionar al menos el siguiente minuto.
      // Validado basado en la zona horaria de la empresa.
      if (moment.tz(form.scheduledAt, companyTimezone).isBefore(moment().tz(companyTimezone).add(3, 'minutes'))) {
        throw new Error("La fecha y hora programada debe ser al menos 3 minutos en el futuro segun la zona horaria de la empresa.");
      }

      const payload = {
        ...form,
        whatsappId: form.whatsappId || (selectedWhatsapp === "all" ? "" : selectedWhatsapp),
        isCommunity: false,
        scheduledAt: moment.tz(form.scheduledAt, companyTimezone).toISOString()
      };
      
      if (!payload.whatsappId || payload.whatsappId === "all") {
        throw new Error("Por favor selecciona una conexión de WhatsApp específica en el filtro superior antes de crear una programación.");
      }

      if (editingId) {
        await api.put(`/group-schedule/${editingId}`, payload);
      } else {
        await api.post("/group-schedule", payload);
      }
      setModalOpen(false);
      showSnack(i18n.t("statusScheduler.saved") || "Programación guardada exitosamente");
      loadSchedules();
    } catch (err) {
      showSnack(getErrorMessage(err), "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(i18n.t("statusScheduler.confirmDelete") || "¿Seguro que deseas eliminar esta programación?")) return;
    try {
      await api.delete(`/group-schedule/${id}`);
      showSnack(i18n.t("statusScheduler.deleted") || "Eliminada correctamente");
      loadSchedules();
    } catch (err) {
      showSnack(getErrorMessage(err, "statusScheduler.errorDelete"), "error");
    }
  };

  const handleToggleActive = async (s) => {
    try {
      await api.put(`/group-schedule/${s.id}`, { isActive: !s.isActive });
      loadSchedules();
    } catch (err) {
      showSnack(getErrorMessage(err, "statusScheduler.errorSave"), "error");
    }
  };

  const MediaIcon = ({ type }) => {
    if (type === "image") return <ImageIcon fontSize="small" style={{ color: "#43a047" }} />;
    if (type === "video") return <VideocamIcon fontSize="small" style={{ color: "#e53935" }} />;
    if (type === "document") return <InsertDriveFileIcon fontSize="small" style={{ color: "#fb8c00" }} />;
    if (type === "audio") return <AudiotrackIcon fontSize="small" style={{ color: "#8e24aa" }} />;
    return <TextFieldsIcon fontSize="small" style={{ color: "#1976d2" }} />;
  };

  const hasActiveGroups = availableGroups.length > 0;

  const filteredSchedules = schedules.filter(s => {
    const isVideo = s.mediaType === "video";
    if (tabValue === 0) return !s.aiGeneration;
    if (tabValue === 1) return s.aiGeneration && !isVideo;
    if (tabValue === 2) return s.aiGeneration && isVideo;
    return true;
  });

  // getBase simple para localhost
  const getBase = () => process.env.REACT_APP_BACKEND_URL || '';
  const getMediaUrl = (relPath, companyId) => {
    const base = getBase();
    if (!relPath) return '';
    if (relPath.startsWith('http')) return relPath;
    
    // Si la ruta contiene 'public/', extraer lo que hay después para evitar rutas dobles
    if (relPath.includes('public/')) {
        return `${base}/public/${relPath.split('public/')[1]}`;
    }
    
    if (relPath.startsWith('ai_img_')) return `${base}/public/company${companyId || 1}/campaigns/${relPath.split('/').pop()}`;
    if (relPath.startsWith('/campaigns/')) return `${base}/public/company${companyId || 1}${relPath}`;
    
    // Fallback: asumir que es un archivo subido normalmente a la carpeta files de la empresa
    return `${base}/public/company${companyId || 1}/files/${relPath.split('/').pop()}`;
  };

  // ── Años disponibles
  const yearsWithCards = [...new Set(schedules.map(s => moment(s.scheduledAt).tz(companyTimezone).year()))];
  const nowYear2 = moment().tz(companyTimezone).year();
  const availableYears = [...new Set([...yearsWithCards, nowYear2, nowYear2 + 1, nowYear2 + 2])].sort((a, b) => a - b);

  // ── Filtrar por año y mes
  const schedulesForMonth = filteredSchedules.filter(s => {
    const m = moment(s.scheduledAt).tz(companyTimezone);
    return m.year() === selectedYear && m.month() === selectedMonth;
  });

  // ── Generar bloques de semana del mes
  const monthStart = moment().tz(companyTimezone).year(selectedYear).month(selectedMonth).startOf('month');
  const monthEnd = monthStart.clone().endOf('month');
  const weekBlocks = [];
  let weekNum = 1;
  let weekCursor = monthStart.clone().startOf('isoWeek');
  while (weekCursor.isSameOrBefore(monthEnd, 'day')) {
    const weekStart = weekCursor.clone();
    const weekEnd = weekCursor.clone().endOf('isoWeek');
    if (weekEnd.isSameOrAfter(monthStart, 'day') && weekStart.isSameOrBefore(monthEnd, 'day')) {
      const effectiveStart = moment.max(weekStart, monthStart);
      const effectiveEnd = moment.min(weekEnd, monthEnd);
      weekBlocks.push({
        key: `sem-${weekNum}`,
        label: `${i18n.t("statusScheduler.weekWord")} ${weekNum} (${effectiveStart.format('DD MMM')} - ${effectiveEnd.format('DD MMM')})`,
        start: weekStart.clone(),
        effectiveStart: effectiveStart.clone(),
        effectiveEnd: effectiveEnd.clone(),
        weekNum,
        items: schedulesForMonth.filter(s => {
          const d = moment(s.scheduledAt).tz(companyTimezone);
          return d.isSameOrAfter(effectiveStart, 'day') && d.isSameOrBefore(effectiveEnd, 'day');
        })
      });
      weekNum++;
    }
    weekCursor.add(1, 'week');
  }

  const connectionSelected = selectedWhatsapp && selectedWhatsapp !== "all";

  return (
    <MainContainer>
      <Paper className={classes.mainPaper} variant="outlined">
        <MainHeader>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <PageHelpGuide
              helpData={GROUP_HELP_DATA}
              tooltipText="Guía y Ayuda de Grupos (Haz clic para ver)"
            />
            <Title>
              {i18n.t("statusScheduler.groupSchedulerTitle") || "Publicar en Grupos"}
              <span className={classes.exclusiveBadge}>{i18n.t("statusScheduler.onlyBaileys") || "Solo Baileys"}</span>
            </Title>
          </div>
          <MainHeaderButtonsWrapper>
            {/* Action buttons moved inside the tab area */}
          </MainHeaderButtonsWrapper>
        </MainHeader>

        <Box style={{ padding: "0 16px 16px" }}>
          <Alert severity="info" variant="outlined" style={{ borderRadius: 8, marginBottom: 16 }}>
            <span dangerouslySetInnerHTML={{ __html: i18n.t("statusScheduler.alertConfigRequiredGroups") }}></span>
          </Alert>
          <Alert severity="success" variant="outlined" style={{ borderRadius: 8, marginBottom: 16 }}>
            <span dangerouslySetInnerHTML={{ __html: i18n.t("statusScheduler.aiDownloadInfo") }} />
          </Alert>
        </Box>

        <Box display="flex" alignItems="center" px={2} mt={1} mb={1} style={{ gap: 16 }}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 250 }}>
            <InputLabel>{i18n.t("statusScheduler.selectConnection")}</InputLabel>
            <Select
              value={selectedWhatsapp}
              onChange={e => setSelectedWhatsapp(e.target.value)}
              label={i18n.t("statusScheduler.selectConnection")}
            >
              <MenuItem value="all">{i18n.t("statusScheduler.allConnections") || "Todas las conexiones"}</MenuItem>
              {connections.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title={i18n.t("statusScheduler.syncTooltip") || "Sincronizar y limpiar grupos fantasma"}>
            <span>
              <Button
                onClick={handleSyncGroups}
                disabled={syncingGroups || !selectedWhatsapp || selectedWhatsapp === "all"}
                startIcon={syncingGroups ? <CircularProgress size={20} /> : <SyncIcon />}
                className={classes.secondaryButton}
                style={{ height: 36 }}
              >
                {i18n.t("statusScheduler.syncGroups") || "Sincronizar"}
              </Button>
            </span>
          </Tooltip>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} px={2} style={{ borderBottom: "1px solid rgba(128,128,128,0.2)", flexWrap: "wrap", gap: 16, paddingBottom: 12 }}>
          <StandardTabs
            value={tabValue}
            onChange={handleTabChange}
            items={[
              {
                value: 0,
                label: i18n.t("statusScheduler.tabs.manual") || "Publicaciones Manuales",
                icon: <EditIcon style={{ color: "#00E676" }} />
              },
              {
                value: 1,
                label: i18n.t("statusScheduler.tabs.aiImage") || "Publicaciones con IA (Imagen)",
                icon: <ImageIcon style={{ color: "#00E5FF" }} />
              },
              {
                value: 2,
                label: "Publicaciones con IA (Video)",
                badge: "BETA",
                icon: <VideocamIcon style={{ color: "#E040FB" }} />
              }
            ]}
          />

          <Button
            variant="contained"
            className={classes.applyButton}
            startIcon={<AddIcon />}
            onClick={() => {
              if (tabValue === 1) {
                setAiFactoryOpen(true);
              } else if (tabValue === 2) {
                setAiVideoOpen(true);
              } else {
                openAdd();
              }
            }}
            disabled={!selectedWhatsapp || selectedWhatsapp === "all" || !hasActiveGroups}
            id="btn-add-group-schedule"
          >
            {tabValue === 1 || tabValue === 2 ? i18n.t("statusScheduler.newAiPost") : (i18n.t("statusScheduler.groupSchedulerAdd") || "Agregar Programación")}
          </Button>
        </Box>

      {/* Selectores Año / Mes */}
      <Box display="flex" alignItems="center" px={2} pb={1} style={{ gap: 12 }}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
          <InputLabel>{i18n.t("statusScheduler.year")}</InputLabel>
          <Select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} label={i18n.t("statusScheduler.year")}>
            {availableYears.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>{i18n.t("statusScheduler.month")}</InputLabel>
          <Select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} label={i18n.t("statusScheduler.month")}>
            {Array.from({ length: 12 }, (_, i) => (
              <MenuItem key={i} value={i}>{moment().month(i).format('MMMM')}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {connectionSelected && (
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Tooltip title={i18n.t("statusScheduler.exportMonth")}>
              <span>
                <IconButton size="small" onClick={() => handleExportWeek(schedulesForMonth, `${moment().month(selectedMonth).format('MMMM')}_${selectedYear}`)} disabled={schedulesForMonth.length === 0} style={{ color: schedulesForMonth.length === 0 ? "#555" : "#26D0CE" }}>
                  <GetAppIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={i18n.t("statusScheduler.importMonth")}>
              <span>
                <IconButton size="small" onClick={() => bulkFileInputRef.current && bulkFileInputRef.current.click()} style={{ color: "#26D0CE" }}>
                  <PublishIcon fontSize="small" />
                </IconButton>
                <input type="file" ref={bulkFileInputRef} style={{ display: 'none' }} accept=".json" onChange={e => handleImportBulkFile(e)} />
              </span>
            </Tooltip>
          </Box>
        )}
        
        <Typography variant="body2" style={{ color: "#cccccc" }}>
          {connectionSelected 
            ? i18n.t("statusScheduler.cardsInMonth", { count: schedulesForMonth.length, month: moment().month(selectedMonth).format('MMMM'), year: selectedYear }) 
            : i18n.t("statusScheduler.selectConnectionToView")}
        </Typography>
      </Box>

      <div className={classes.scrollArea}>
      {!connectionSelected ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={6} style={{ gap: 12, color: "#888" }}>
          <Typography variant="h6" style={{ color: "#555" }}>{i18n.t("statusScheduler.selectConnectionTitleWp")}</Typography>
          <Typography variant="body2">{i18n.t("statusScheduler.selectConnectionDescGroups")}</Typography>
        </Box>
      ) : loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <Box style={{ padding: 16 }}>
          {weekBlocks.length === 0 && (
            <Typography style={{ textAlign: "center", color: "#888", marginTop: 20 }}>
              {i18n.t("statusScheduler.emptyMonth", { month: moment().month(selectedMonth).format('MMMM'), year: selectedYear })}
            </Typography>
          )}
          {weekBlocks.map(block => {
            const weekFileRef = React.createRef();
            const isWeekPast = block.effectiveEnd.clone().endOf('day').isBefore(moment().tz(companyTimezone));
            const isWeekCurrent = !isWeekPast && block.effectiveStart.isSameOrBefore(moment().tz(companyTimezone), 'day');
            return (
              <Accordion key={block.key} defaultExpanded={isWeekCurrent} style={{ marginBottom: 16, background: "rgba(255,255,255,0.03)", opacity: isWeekPast ? 0.85 : 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.weekHeader}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Typography style={{ fontWeight: 'bold', color: isWeekPast ? '#bbbbbb' : '#00BFA5' }}>{block.label}</Typography>
                      <Typography variant="caption" style={{ color: "#cccccc" }}>{block.items.length === 1 ? i18n.t("statusScheduler.cardsCountSingular", {count: 1}) : i18n.t("statusScheduler.cardsCountPlural", {count: block.items.length})}</Typography>
                      {isWeekPast && <Typography variant="caption" style={{ color: "#ff7043", fontWeight: 'bold' }}>{i18n.t("statusScheduler.statusFinished")}</Typography>}
                      {isWeekCurrent && <Typography variant="caption" style={{ color: "#66bb6a", fontWeight: 'bold' }}>{i18n.t("statusScheduler.statusOngoing")}</Typography>}
                    </Box>
                    <Box onClick={e => e.stopPropagation()}>
                      {!isWeekPast && (
                        <>
                          <Tooltip title={i18n.t("statusScheduler.importWeekTooltip")}>
                            <IconButton size="small" onClick={() => weekFileRef.current && weekFileRef.current.click()} style={{ color: "#26D0CE", marginRight: 4 }}>
                              <PublishIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <input type="file" ref={weekFileRef} style={{ display: 'none' }} accept=".json"
                            onChange={e => handleImportBulkFile(e, block.start.clone())} />
                        </>
                      )}
                      <Tooltip title={block.items.length === 0 ? i18n.t("statusScheduler.noCardsToExport") : i18n.t("statusScheduler.exportWeek")}>
                        <span>
                          <IconButton size="small" onClick={() => handleExportWeek(block.items, block.label)} disabled={block.items.length === 0} style={{ color: block.items.length === 0 ? "#555" : "#26D0CE" }}>
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  {block.items.length === 0 ? (
                    <Typography style={{ color: "#666", fontSize: "0.85rem", textAlign: 'center', width: '100%' }}>
                      {isWeekPast ? i18n.t("statusScheduler.noCardsInWeekFinished") : i18n.t("statusScheduler.noCardsInWeek")}
                    </Typography>
                  ) : (
                  <Grid container spacing={2}>
                    {block.items.map(s => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={s.id}>
              <Box
                className={`${classes.scheduleCard} ${!s.isActive || s.status === 'SENT' ? classes.inactiveCard : ""}`}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center">
                    <MediaIcon type={s.mediaType} />
                    <Typography color="textPrimary" style={{ fontSize: "0.9rem", fontWeight: "bold", marginLeft: 8 }}>
                      {moment(s.scheduledAt).tz(companyTimezone).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  </Box>
                  <Box>
                    <Tooltip title={i18n.t("statusScheduler.clone") || "Clonar"}>
                      <span>
                        <IconButton size="small" onClick={() => openClone(s)} color="inherit">
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {s.status === 'SENT' ? (
                      <Tooltip title={i18n.t("statusScheduler.view") || "Visualizar"}>
                        <span>
                          <IconButton size="small" onClick={() => setViewSchedule(s)} id={`btn-view-${s.id}`} style={{ color: "#1976d2" }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={i18n.t("statusScheduler.edit") || "Editar"}>
                        <span>
                          <IconButton size="small" onClick={() => {
                            if (s.aiGeneration) {
                              setAiEditSchedule(s);
                              setAiFactoryOpen(true);
                            } else {
                              setEditingId(s.id);
                              openEdit(s);
                            }
                          }} color="inherit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title={i18n.t("statusScheduler.delete") || "Eliminar"}>
                      <IconButton size="small" onClick={() => handleDelete(s.id)} style={{ color: "#f44336" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                
                <Box>
                    {s.status === 'SENT' && (
                    <span className={classes.sentBadge}>{i18n.t("statusScheduler.sent") || "Enviado"}</span>
                    )}
                </Box>

                <Box display="flex" style={{ gap: 12, marginTop: 8 }}>
                  {s.mediaUrl && (
                    <Box 
                      flexShrink={0} 
                      style={{ position: 'relative', cursor: 'pointer' }}
                      onClick={() => setPreviewMedia({ url: s.mediaUrl, type: s.mediaType, companyId: s.companyId })}
                    >
                      {(s.mediaType === "image" || (!s.mediaType && s.mediaUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(s.mediaUrl))) && (
                        <img
                          src={getMediaUrl(s.mediaUrl, s.companyId)}
                          alt="Status Preview"
                          style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 4, border: "1px solid #ddd" }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}
                      {(s.mediaType === "video" || (!s.mediaType && s.mediaUrl && /\.(mp4|webm|ogg)$/i.test(s.mediaUrl))) && (
                        <video
                          src={`${getMediaUrl(s.mediaUrl, s.companyId)}#t=0.1`}
                          style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 4, border: "1px solid #ddd", backgroundColor: "#000" }}
                          muted
                          preload="metadata"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', borderRadius: 4 }}>
                        <ZoomInIcon style={{ color: 'white', fontSize: 32 }} />
                      </div>
                    </Box>
                  )}
                  
                  <Box overflow="hidden" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography color="textSecondary" style={{ fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center" }} title={s.groupIds?.map(id => availableGroups.find(g => id.includes(g.number))?.name || id.replace('@g.us', '')).join(", ")}>
                      <GroupIcon fontSize="small" style={{ marginRight: 6, opacity: 0.7 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.groupIds?.map(id => availableGroups.find(g => id.includes(g.number))?.name || id.replace('@g.us', '')).join(", ")}
                      </span>
                    </Typography>
                    
                    {s.caption && (
                      <Typography color="textPrimary" style={{ fontSize: "0.85rem", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2 }} title={s.caption}>
                        <AiPromptViewer prompt={s.caption} companyId={s.companyId} compact={true} />
                        </Typography>
                    )}

                    {s.mediaUrl && (
                      <Typography color="textSecondary" style={{ fontSize: "0.75rem", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        📎 {s.mediaUrl.split("/").pop()}
                      </Typography>
                    )}
                  </Box>
                </Box>
                  {(() => {
                    const connName = s.whatsapp?.name || connections.find(c => c.id === s.whatsappId)?.name || (s.whatsappId === "all" ? i18n.t("statusScheduler.allConnections") : "");
                    return connName ? (
                      <Box mt={1} mb={1}>
                        <span style={{ fontSize: "0.75rem", background: "#eee", color: "#333", padding: "2px 6px", borderRadius: 4, fontWeight: "bold" }}>
                          📱 {connName}
                        </span>
                      </Box>
                    ) : null;
                  })()}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={s.isActive}
                      onChange={() => handleToggleActive(s)}
                      color="primary"
                      disabled={s.status === 'SENT'}
                    />
                  }
                  label={<Typography color="textSecondary" style={{ fontSize: "0.8rem" }}>{s.isActive ? (i18n.t("statusScheduler.active") || "Activo") : (i18n.t("statusScheduler.inactive") || "Inactivo")}</Typography>}
                  style={{ marginTop: 8 }}
                />
              </Box>
            </Grid>
                ))}
                  </Grid>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
      </div>

      <AiContentFactory
        open={aiFactoryOpen}
        onClose={() => { setAiFactoryOpen(false); setAiEditSchedule(null); }}
        selectedWhatsapp={selectedWhatsapp}
        availableGroups={availableGroups}
        onScheduled={() => { loadSchedules(); setAiFactoryOpen(false); }}
        onRefreshGrid={() => loadSchedules()}
        initialQuantity={1}
        editSchedule={aiEditSchedule}
        type="group"
        restrictedGroups={restrictedGroups}
        syncingGroups={syncingGroups}
        onSyncGroups={handleSyncGroups}
      />

      {/* Modal Agregar/Editar */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle>
          {editingId ? (i18n.t("statusScheduler.editGroupTitle") || "Editar Programación de Grupos") : (i18n.t("statusScheduler.addGroupTitle") || "Nueva Programación de Grupos")}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            
            {/* Grupos destino */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <Autocomplete
                  multiple
                  size="small"
                  style={{ flexGrow: 1 }}
                  noOptionsText={i18n.t("statusScheduler.noOptions") || "Sin resultados"}
                  options={availableGroups.filter((group) => !restrictedGroups[group.id]?.isCommunity)}
                  getOptionLabel={(option) => option.name}
                  getOptionDisabled={(option) => restrictedGroups[option.id]?.isRestricted}
                  value={availableGroups.filter(g => form.groupIds.includes(`${g.number}@g.us`))}
                  onChange={(event, newValue) => {
                    setForm(f => ({ ...f, groupIds: newValue.map(v => `${v.number}@g.us`) }));
                  }}
                  disableCloseOnSelect
                  renderOption={(option, { selected }) => (
                    <React.Fragment>
                      <Checkbox
                        color="primary"
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name} {restrictedGroups[option.id]?.isRestricted && <span style={{ marginLeft: 6 }} title="Bot no tiene permisos">🔒</span>}
                    </React.Fragment>
                  )}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      variant="outlined" 
                      label={i18n.t("statusScheduler.targetGroups") || "Grupos destino"} 
                      placeholder={i18n.t("statusScheduler.searchGroups") || "Buscar grupos..."}
                    />
                  )}
                />
                <Tooltip title={i18n.t("statusScheduler.syncTooltip") || "Sincronizar y limpiar grupos fantasma"}>
                  <Box ml={1}>
                    <IconButton 
                      color="primary" 
                      onClick={handleSyncGroups} 
                      disabled={syncingGroups || !selectedWhatsapp}
                      style={{ border: '1px solid rgba(0,0,0,0.1)', padding: 8 }}
                    >
                      {syncingGroups ? <CircularProgress size={24} /> : <SyncIcon />}
                    </IconButton>
                  </Box>
                </Tooltip>
              </Box>
            </Grid>

            {/* Día y Hora (Datetime) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="datetime-local"
                label={i18n.t("statusScheduler.time") || "Fecha y Hora"}
                value={form.scheduledAt}
                onChange={e => { const val = e.target.value; setForm(f => ({ ...f, scheduledAt: val })); }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Tipo de medio */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>{i18n.t("statusScheduler.mediaType")}</InputLabel>
                <Select
                  value={form.mediaType}
                  onChange={e => { const val = e.target.value; setForm(f => ({ ...f, mediaType: val, mediaUrl: "", caption: "" })); }}
                  label={i18n.t("statusScheduler.mediaType")}
                >
                  <MenuItem value="text">📝 {i18n.t("statusScheduler.typeText")}</MenuItem>
                  <MenuItem value="image">🖼️ {i18n.t("statusScheduler.typeImage")}</MenuItem>
                  <MenuItem value="video">🎬 {i18n.t("statusScheduler.typeVideo")}</MenuItem>
                  <MenuItem value="document">📄 {i18n.t("statusScheduler.typeDocument") || "Documento"}</MenuItem>
                  <MenuItem value="audio">🎵 {i18n.t("statusScheduler.typeAudio") || "Audio"}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Texto */}
            {form.mediaType === "text" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" variant="outlined" multiline minRows={3} maxRows={10}
                  label={i18n.t("statusScheduler.caption") || "Mensaje de texto"}
                  value={form.caption}
                  onChange={e => { const val = e.target.value; setForm(f => ({ ...f, caption: val })); }}
                />
                <Typography style={{ fontSize: "0.75rem", color: "#00e5ff", textShadow: "0px 0px 4px rgba(0,229,255,0.6)", marginTop: 4 }}>
                  {i18n.t("statusScheduler.markdownTip")}
                </Typography>
              </Grid>
            )}

            {/* Imagen, Video, Documento o Audio: selector de Archivos */}
            {(form.mediaType === "image" || form.mediaType === "video" || form.mediaType === "document" || form.mediaType === "audio") && (
              <>
                <Grid item xs={12}>
                  <Box className={classes.fileInputRow}>
                    <TextField
                      fullWidth size="small" variant="outlined"
                      label={i18n.t("statusScheduler.fileUrl")}
                      placeholder="/files/mi-imagen.png"
                      value={form.mediaUrl}
                      onChange={e => { const val = e.target.value; setForm(f => ({ ...f, mediaUrl: val })); }}
                    />
                    <Tooltip title={i18n.t("statusScheduler.openFileManager")}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FolderOpenIcon />}
                        onClick={() => setSelectorModalOpen(true)}
                        style={{ whiteSpace: "nowrap", padding: "0 20px" }}
                      >
                        {i18n.t("statusScheduler.files")}
                      </Button>
                    </Tooltip>
                  </Box>
                  <Typography style={{ fontSize: "0.75rem", color: "#888", marginTop: 4 }}>
                    {i18n.t("statusScheduler.fileHint")}
                  </Typography>
                  {form.mediaUrl && (form.mediaType === "image" || form.mediaType === "video") && (
                    <Box mt={2} display="flex" justifyContent="center">
                      <img 
                        src={form.mediaUrl.startsWith("http") ? form.mediaUrl : `${process.env.REACT_APP_BACKEND_URL}/public/${form.mediaUrl.replace(/^\/?(public\/)?/, "")}`} 
                        alt="Preview" 
                        style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, objectFit: "contain", border: "1px solid #ddd" }}
                        onError={(e) => {
                          if (e.target.dataset.tried) {
                              e.target.style.display = "none";
                              return;
                          }
                          e.target.dataset.tried = "true";
                          e.target.src = `${process.env.REACT_APP_BACKEND_URL}/public/company1/files/${form.mediaUrl.split("/").pop()}`;
                        }}
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" variant="outlined" multiline minRows={2} maxRows={8}
                    label={i18n.t("statusScheduler.captionOptional") || "Leyenda (Opcional)"}
                    value={form.caption}
                    onChange={e => { const val = e.target.value; setForm(f => ({ ...f, caption: val })); }}
                  />
                  <Typography style={{ fontSize: "0.85rem", fontWeight: "bold", color: "#ccff00", textShadow: "0px 0px 8px rgba(204,255,0,0.8)", marginTop: 8 }}>
                    {i18n.t("statusScheduler.markdownTip")}
                  </Typography>
                </Grid>
              </>
            )}

            {/* Mention All & Interval */}
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.mentionAll}
                    onChange={e => { const checked = e.target.checked; setForm(f => ({ ...f, mentionAll: checked })); }}
                    color="primary"
                  />
                }
                label={i18n.t("statusScheduler.mentionAll") || "Mencionar a todos (@todos)"}
              />
              <Typography variant="caption" style={{ display: 'block', marginTop: 4, lineHeight: 1.2, color: '#ff8a80', fontWeight: 'bold' }}>
                {i18n.t("statusScheduler.mentionSpamWarning")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="number"
                label={i18n.t("statusScheduler.sendInterval") || "Pausa entre envíos (segs)"}
                value={form.sendInterval}
                onChange={e => { 
                  let val = parseInt(e.target.value, 10);
                  if (isNaN(val)) val = 15;
                  if (val < 15) val = 15;
                  if (val > 120) val = 120;
                  setForm(f => ({ ...f, sendInterval: val })); 
                }}
                inputProps={{ min: 15, max: 120 }}
              />
            </Grid>

            {/* Activo */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={e => { const checked = e.target.checked; setForm(f => ({ ...f, isActive: checked })); }}
                    color="primary"
                  />
                }
                label={i18n.t("statusScheduler.isActive")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setModalOpen(false)} className={classes.secondaryButton}>
            {i18n.t("statusScheduler.cancel") || "Cancelar"}
          </Button>
          {form.status !== 'SENT' && (
            <Button onClick={handleSave} className={classes.applyButton} disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : i18n.t("statusScheduler.save") || "Guardar"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(previewMedia)} onClose={() => setPreviewMedia(null)} maxWidth="md" fullWidth PaperProps={{ className: classes.dialogPaper }}>
        <Box display="flex" justifyContent="center" alignItems="center" style={{ background: "#000", minHeight: 300, position: 'relative' }}>
          {previewMedia && previewMedia.type === "video" ? (
            <video 
              src={getMediaUrl(previewMedia.url, previewMedia.companyId)}
              controls 
              autoPlay
              style={{ width: "100%", maxHeight: "85vh", objectFit: "contain" }} 
            />
          ) : previewMedia && (
            <img 
              src={getMediaUrl(previewMedia.url, previewMedia.companyId)}
              alt="Preview" 
              style={{ width: "100%", maxHeight: "85vh", objectFit: "contain" }} 
            />
          )}
        </Box>
      </Dialog>

      <FileManagerSelectorModal
        open={selectorModalOpen}
        onClose={() => setSelectorModalOpen(false)}
        onSelect={(path) => setForm(f => ({ ...f, mediaUrl: path }))}
        filterType={form.mediaType}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
      {/* Modal Ver Contenido (solo lectura para tarjetas ENVIADAS) */}
      <Dialog open={Boolean(viewSchedule)} onClose={() => setViewSchedule(null)} maxWidth="sm" fullWidth PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon style={{ color: '#1976d2', marginRight: 8 }} />
            Ver contenido enviado
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {viewSchedule && (
            <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              {viewSchedule.mediaUrl && (viewSchedule.mediaType === 'image' || (!viewSchedule.mediaType && /\.(jpg|jpeg|png|gif|webp)$/i.test(viewSchedule.mediaUrl))) && (
                <Box textAlign="center">
                  <img
                    src={getMediaUrl(viewSchedule.mediaUrl, viewSchedule.companyId)}
                    alt="Contenido"
                    style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 8, border: '1px solid #ddd' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </Box>
              )}
              {viewSchedule.caption && (
                <Box>
                  <Typography variant="caption" style={{ fontWeight: 700, color: '#00e5ff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '0 0 5px rgba(0,229,255,0.4)' }}>{i18n.t("statusScheduler.modalTextCaption")}</Typography>
                  <Box
                    style={{
                      marginTop: 6,
                      background: 'rgba(128, 128, 128, 0.1)',
                      borderRadius: 6,
                      padding: '10px 14px',
                      fontFamily: 'monospace',
                      fontSize: '0.92rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    <AiPromptViewer prompt={viewSchedule.caption} companyId={viewSchedule.companyId} />
                  </Box>
                </Box>
              )}
              {viewSchedule.aiGeneration && viewSchedule.aiPrompt && (
                <Box>
                  <Typography variant="caption" style={{ fontWeight: 700, color: '#00e5ff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '0 0 5px rgba(0,229,255,0.4)' }}>{i18n.t("statusScheduler.modalAiPrompt")}</Typography>
                  <Box
                    style={{
                      marginTop: 6,
                      background: 'rgba(156, 39, 176, 0.1)',
                      borderLeft: '4px solid #9c27b0',
                      borderRadius: '0 6px 6px 0',
                      padding: '10px 14px',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    <AiPromptViewer prompt={viewSchedule.aiPrompt} companyId={viewSchedule.companyId} />
                  </Box>
                </Box>
              )}
              <Box display="flex" style={{ gap: 24, flexWrap: 'wrap' }}>
                {viewSchedule.scheduledAt && (
                  <Box>
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#00e5ff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '0 0 5px rgba(0,229,255,0.4)' }}>{i18n.t("statusScheduler.modalDateTime")}</Typography>
                    <Typography>{viewSchedule.scheduledAt}</Typography>
                  </Box>
                )}
                {viewSchedule.mediaUrl && (
                  <Box>
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#00e5ff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '0 0 5px rgba(0,229,255,0.4)' }}>{i18n.t("statusScheduler.modalFile")}</Typography>
                    <Typography style={{ fontSize: '0.82rem', wordBreak: 'break-all' }}>📎 {viewSchedule.mediaUrl.split("/").pop()}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          {viewSchedule?.caption && (
            <Button 
              onClick={() => { navigator.clipboard.writeText(viewSchedule.caption); }} 
              className={classes.secondaryButton}
              style={{ marginRight: 'auto' }}
            >
              Copiar texto
            </Button>
          )}
          <Button 
            onClick={() => setViewSchedule(null)} 
            className={classes.dangerButton}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      <AiVideoModal open={aiVideoOpen} onClose={() => setAiVideoOpen(false)} onVideoGenerated={handleVideoGenerated} />
      </Paper>

      {/* Modal Importacion Masiva */}
      <Dialog open={importBulkOpen} onClose={() => { setImportBulkOpen(false); setImportSummary(null); }} maxWidth="sm" fullWidth PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle>{i18n.t("statusScheduler.importCards") || "Importar Tarjetas"}</DialogTitle>
        <DialogContent>
          {importSummary && (
            <Box mb={2} p={1.5} style={{ background: "rgba(0,191,165,0.08)", borderRadius: 8, border: "1px solid rgba(0,191,165,0.3)" }}>
              <Typography variant="body2" gutterBottom>{i18n.t("statusScheduler.origin")}: {importSummary.originLabel}</Typography>
              <Typography variant="body2" gutterBottom>{i18n.t("statusScheduler.totalCards")}: <b>{importSummary.total}</b></Typography>
              <Typography variant="body2" gutterBottom>{i18n.t("statusScheduler.destination")}: <b>{importSummary.destLabel}</b></Typography>
              <Typography variant="body2" gutterBottom style={{ fontWeight: 'bold', marginTop: 8 }}>{i18n.t("statusScheduler.estimatedDistribution")}:</Typography>
              {Object.entries(importSummary.byWeek).map(([wk, count]) => (
                <Typography key={wk} variant="body2" style={{ marginLeft: 12, color: "#aaa" }}>
                  {wk}: {count} tarjeta{count !== 1 ? 's' : ''}
                </Typography>
              ))}
              {importSummary.clampedCount > 0 && (
                <Typography variant="body2" style={{ marginTop: 8, color: "#ff7043" }}>
                  {i18n.t("statusScheduler.cardsClamped", { count: importSummary.clampedCount })}
                </Typography>
              )}
              <Typography variant="body2" style={{ marginTop: 8, color: "#ff9800" }}>
                {i18n.t("statusScheduler.importWarningInactives")} {!importSummary.isSameCompany && i18n.t("statusScheduler.importWarningImages")}
              </Typography>
            </Box>
          )}
          <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 16 }}>
            <InputLabel>Conexion WhatsApp</InputLabel>
            <Select value={importBulkForm.whatsappId} onChange={e => setImportBulkForm(f => ({ ...f, whatsappId: e.target.value }))} label="Conexion WhatsApp">
              {connections.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            options={availableGroups.filter(g => !restrictedGroups[g.id]?.isCommunity)}
            getOptionLabel={o => o.name || o.number}
            value={availableGroups.filter(g => importBulkForm.groupIds.includes(`${g.number}@g.us`))}
            onChange={(_, newValue) => setImportBulkForm(f => ({ ...f, groupIds: newValue.map(v => `${v.number}@g.us`) }))}
            renderInput={params => <TextField {...params} variant="outlined" label="Grupos destino" size="small" />}
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => { setImportBulkOpen(false); setImportSummary(null); }} className={classes.secondaryButton}>
            Cancelar
          </Button>
          <Button className={classes.applyButton} onClick={processBulkImport} disabled={importingBulk}>
            {importingBulk ? <CircularProgress size={20} /> : i18n.t("statusScheduler.importButton", { count: importSummary?.total || "" })}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
}
